import { useEffect, useState } from "react";
import { toast } from "sonner";
import { ChevronLeft, UserPlus, KeyRound, Trash2, ShieldCheck } from "lucide-react";
import { listUsers, inviteUser, resetUser, removeUser, formatApiError } from "../lib/api";

export default function Members({ onBack }) {
  const [users, setUsers] = useState(null);
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [confirmId, setConfirmId] = useState(null); // member pending removal

  const load = () => listUsers().then(setUsers).catch(() => setUsers([]));
  useEffect(() => {
    load();
  }, []);

  const handleInvite = async () => {
    const e = email.trim().toLowerCase();
    if (!e) return;
    setBusy(true);
    try {
      await inviteUser(e);
      setEmail("");
      toast.success(`Invited ${e}`);
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const handleReset = async (u) => {
    try {
      await resetUser(u.id);
      toast.success(`${u.email} can set a new password at their next sign-in.`);
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  const handleRemove = async (u) => {
    try {
      await removeUser(u.id);
      setConfirmId(null);
      toast(`Removed ${u.email}`);
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  return (
    <div className="min-h-screen">
      <header className="glass sticky top-0 z-30 border-b border-white/5">
        <div className="mx-auto max-w-3xl px-5 sm:px-8 py-3.5">
          <button
            onClick={onBack}
            className="inline-flex items-center gap-1 rounded-lg py-1.5 pr-3 text-[17px] font-medium text-[#0A84FF] hover:opacity-80 transition-opacity"
            data-testid="members-back-button"
          >
            <ChevronLeft className="h-6 w-6" />
            Videos
          </button>
        </div>
      </header>

      <main className="mx-auto max-w-3xl px-5 sm:px-8 pb-20">
        <h1 className="mt-5 text-3xl font-bold tracking-tight text-white">People</h1>
        <p className="mt-2 mb-8 text-lg text-white/50">
          Invite family members and manage who can use the app.
        </p>

        {/* Invite */}
        <div className="rounded-3xl bg-[#1c1c1e] p-5 ring-1 ring-white/10">
          <p className="mb-3 text-[15px] font-medium text-white/50">Invite someone by email</p>
          <div className="flex flex-col sm:flex-row gap-3">
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && handleInvite()}
              placeholder="name@example.com"
              className="h-14 flex-1 rounded-2xl bg-white/[0.06] px-5 text-xl text-white placeholder-white/35 outline-none ring-1 ring-white/10 focus:ring-2 focus:ring-[#0A84FF] transition-all"
              data-testid="invite-email-input"
            />
            <button
              onClick={handleInvite}
              disabled={busy || !email.trim()}
              className="inline-flex h-14 items-center justify-center gap-2 rounded-2xl bg-[#0A84FF] px-7 text-lg font-semibold text-white hover:bg-[#409CFF] transition-colors disabled:opacity-40"
              data-testid="invite-button"
            >
              <UserPlus className="h-5 w-5" />
              Invite
            </button>
          </div>
          <p className="mt-3 text-base text-white/40">
            They'll set their own password the first time they sign in.
          </p>
        </div>

        {/* List */}
        <div className="mt-8 flex flex-col gap-3" data-testid="members-list">
          {users === null ? (
            <p className="text-lg text-white/40 animate-pulse">Loading…</p>
          ) : (
            users.map((u) => (
              <div
                key={u.id}
                className="rounded-3xl bg-[#1c1c1e] p-5 ring-1 ring-white/10"
                data-testid="member-row"
              >
                <div className="flex items-center gap-3">
                  <div className="min-w-0 flex-1">
                    <p className="flex items-center gap-2 text-xl font-semibold text-white truncate">
                      {u.email}
                      {u.role === "admin" && (
                        <span className="inline-flex items-center gap-1 rounded-full bg-[#0A84FF]/20 px-2.5 py-0.5 text-[13px] font-semibold text-[#4DA3FF]">
                          <ShieldCheck className="h-3.5 w-3.5" /> Admin
                        </span>
                      )}
                    </p>
                    <p className={`mt-0.5 text-base ${u.active ? "text-[#30D158]" : "text-[#FF9F0A]"}`}>
                      {u.active ? "Active" : "Needs to set password"}
                    </p>
                  </div>
                  {u.role !== "admin" && (
                    <div className="flex flex-shrink-0 gap-2">
                      <button
                        onClick={() => handleReset(u)}
                        title="Reset password"
                        className="inline-flex h-11 items-center gap-1.5 rounded-xl bg-white/10 px-4 text-base font-medium text-white hover:bg-white/20 transition-colors"
                        data-testid="reset-member-button"
                      >
                        <KeyRound className="h-4 w-4" />
                        Reset
                      </button>
                      <button
                        onClick={() => setConfirmId(u.id)}
                        title="Remove person"
                        className="grid h-11 w-11 place-items-center rounded-xl bg-white/10 text-[#FF453A] hover:bg-[#FF453A]/20 transition-colors"
                        data-testid="remove-member-button"
                      >
                        <Trash2 className="h-5 w-5" />
                      </button>
                    </div>
                  )}
                </div>

                {confirmId === u.id && (
                  <div className="mt-4 flex items-center justify-between gap-3 rounded-2xl bg-[#FF453A]/10 p-4">
                    <span className="text-base text-white">Remove {u.email}?</span>
                    <div className="flex flex-shrink-0 gap-2">
                      <button
                        onClick={() => setConfirmId(null)}
                        className="h-11 rounded-xl bg-white/10 px-4 text-base font-medium text-white hover:bg-white/20"
                        data-testid="cancel-remove-button"
                      >
                        Cancel
                      </button>
                      <button
                        onClick={() => handleRemove(u)}
                        className="h-11 rounded-xl bg-[#FF453A] px-4 text-base font-semibold text-white hover:bg-[#ff5a50]"
                        data-testid="confirm-remove-button"
                      >
                        Remove
                      </button>
                    </div>
                  </div>
                )}
              </div>
            ))
          )}
        </div>
      </main>
    </div>
  );
}
